// 261023 E3-P3h5a (B2 / B11) — chặn chi tiêu của chuỗi "Truyện mới" Ở PHÍA FE khi MỌI SỐ TIỀN BỊ ẨN: khung Bắt đầu, thẻ STOP 1, dải «Chờ bạn quyết», cầu nối «Chạy tiếp».
// THUẦN: KHÔNG import antd / react (jest của react-scripts không load được antd v6). `.tsx` chỉ vẽ + gọi API.
// Luật cứng: KHÔNG chuỗi nào hiện cho người dùng được chứa số tiền / «trần» / «ước tiền» / lý do thô của BE. Số thô chỉ đi trong DỮ LIỆU để gửi BE:
//  - `expectedMaxUsd` (Bắt đầu + STOP 1: người dùng đã «xem» số tối đa — BE vẫn bắt buộc);
//  - `newCapUsd` của «Chạy tiếp» (bảng B11, tính ở `continueDecision`; KHÔNG kẹp xuống ceiling — vượt ceiling ⇒ DỪNG, không gửi).
import type { NewStoryBudgetView, NewStoryContinuation, Stop1Estimate, WaitBudgetStepEstimate, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { NewStoryContinueResult, NewStoryEstimate, NewStoryRaiseCapResult } from '../../../services/api/adaptation';

const EPS = 1e-9;
/** Sàn ceiling (cùng BE `CAP_RAISE_FLOOR_USD`). */
const CAP_RAISE_FLOOR_USD = 50;

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** Làm tròn LÊN xu (0,01). Sai số số thực không đẩy lên một xu. */
export const roundUpCent = (n: number): number => Math.round((Math.ceil(n * 100 - EPS) / 100) * 100) / 100;
/** Làm tròn LÊN bội của 0,05 (sai số số thực không đẩy lên một nấc). */
export const roundUpTo05 = (n: number): number => Math.round((Math.ceil(n * 20 - EPS) / 20) * 100) / 100;
/** Xu kế tiếp NGAY TRÊN n (BE đòi mức mới > mức cũ). */
export const nextCent = (n: number): number => Math.round((Math.floor(n * 100 + EPS) + 1)) / 100;
/** ceiling = max(50, 2 × ước tối đa Chặng 1) — cùng BE `capRaiseCeiling`. Số hỏng ⇒ 50. */
export const capCeilingOf = (estimatedMaxUsd: unknown): number => Math.max(CAP_RAISE_FLOOR_USD, 2 * (isNum(estimatedMaxUsd) && estimatedMaxUsd > 0 ? estimatedMaxUsd : 0));

/** Sổ ở workbench: SAU khi lưu khung xương BE trải `budget` ra gốc; TRƯỚC khi lưu nó nằm ở `newStoryRun.budget`. Gốc thắng. */
export const budgetOf = (v: Pick<WorkbenchView, 'budget' | 'newStoryRun'> | null | undefined): NewStoryBudgetView | null => v?.budget ?? v?.newStoryRun?.budget ?? null;

// ─── Tên bước (không có «Đang») ─────────────────────────────────────────────

const STEP_NAME: Record<string, string> = {
  SCAN: 'quét truyện', SKELETON: 'dựng khung xương', OVERVIEW: 'tạo tổng quan', OVERVIEW_EXTRACT: 'tạo tổng quan', OVERVIEW_MERGE: 'tạo tổng quan',
  LOCALIZE: 'bản địa hoá', ALIAS_LOCALIZE: 'bản địa hoá', SPLIT: 'chia tập', WRITE: 'viết kịch bản',
  SEASON_READER: 'đọc cả mùa',
  LOCALIZE_REL: 'quan hệ nhân vật', LOCALIZE_ALIAS: 'quét bí danh', LOCALIZE_NAME: 'đặt tên bản địa', GATE_LOCALIZE: 'cổng AI bản địa hoá',
  SPLIT_BATCH: 'chia tập theo lô', SPLIT_REPAIR: 'sửa chia tập', GATE_SPLIT: 'cổng AI chia tập',
  SKELETON_JUDGE: 'chấm khung xương', SKELETON_REPAIR: 'sửa khung xương',
  VOLUME_JUDGE: 'chấm bản quét quyển', VOLUME_RESCAN: 'quét lại quyển',
};
/** Nhãn lạ (BE thêm bước mới) ⇒ hiện NGUYÊN chữ; thiếu hẳn ⇒ «bước kế tiếp». */
const stepName = (step: string | null | undefined): string => {
  const t = typeof step === 'string' ? step.trim() : '';
  return t ? (STEP_NAME[t] ?? t) : 'bước kế tiếp';
};

/** Nhãn bước của sổ thuộc Chặng 2 (bản địa hoá → chia tập → viết → đọc cả mùa). */
const CHANG2_STEPS: ReadonlySet<string> = new Set(['LOCALIZE', 'ALIAS_LOCALIZE', 'LOCALIZE_REL', 'LOCALIZE_ALIAS', 'LOCALIZE_NAME', 'GATE_LOCALIZE', 'SPLIT', 'SPLIT_BATCH', 'SPLIT_REPAIR', 'GATE_SPLIT', 'WRITE', 'SEASON_READER']);
const CHANG1_STEPS: ReadonlySet<string> = new Set(['SCAN', 'SKELETON', 'SKELETON_JUDGE', 'SKELETON_REPAIR', 'VOLUME_JUDGE', 'VOLUME_RESCAN', 'OVERVIEW', 'OVERVIEW_EXTRACT', 'OVERVIEW_MERGE']);
/** `appendRun.step` thô cho biết chuỗi đang ở Chặng 2 (dùng khi sổ không còn `blocked` / nhãn bước lạ). */
const CHANG2_RUN_STEPS: ReadonlySet<string> = new Set(['LOCALIZE', 'ALIAS_LOCALIZE', 'SPLIT', 'WRITE', 'SEASON_READER']);

/** Dòng xám khi có lời gọi AI bị đứt giữa chừng. Chỉ nói số lời gọi — không số tiền. */
export const interruptedCallsLine = (b: NewStoryBudgetView | null | undefined): string | null => {
  const n = b?.orphanedCount ?? 0;
  if (!b || !(n > 0)) return null;
  return `Có ${n} lời gọi AI bị đứt giữa chừng. Chạy tiếp có thể phải làm lại phần đó.`;
};

// ─── B11 — «Chạy tiếp» khi chuỗi chờ quyết (WAIT_BUDGET) ─────────────────────

export const CONTINUE_LABEL = 'Chạy tiếp';
export const STOP_UNKNOWN_TEXT = 'Máy chưa tính được mức chi cho bước này — báo kỹ thuật';
export const STOP_CEILING_TEXT = 'Bước này cần mức chi vượt giới hạn an toàn của truyện — báo quản trị';
const STOP_NO_LEDGER_TEXT = 'Chưa đọc được sổ của chuỗi — tải lại trang';

export type ContinueStopCode = 'UNKNOWN' | 'CEILING' | 'NO_LEDGER';
/** Kết quả bảng B11. `raise`: gọi route nâng-và-chạy-tiếp ĐÚNG MỘT LẦN với `newCapUsd` + `expectedRevision`; `resume`: chỉ chạy lại; `stop`: không gọi gì. */
export type ContinueDecision =
  | { kind: 'resume' }
  | { kind: 'raise'; newCapUsd: number; expectedRevision: number }
  | { kind: 'stop'; code: ContinueStopCode; text: string };

/** E3-P3c2b — ngữ cảnh thêm: `runStep` (đoán chặng khi sổ không còn `blocked`), `stepEstimate` (H9: số cần để chạy NỐT bước đang chờ — Chặng 2). */
export interface WaitBudgetExtra {
  runStep?: string | null;
  stepEstimate?: WaitBudgetStepEstimate | null;
  /** 3h5b: BE có route `continue-auto-budget` ⇒ BE quyết định nâng/chạy lại; vắng ⇒ cầu nối 3h5a (FE tính theo B11). */
  continuation?: NewStoryContinuation | null;
}

const isChang2 = (blStep: string, runStep: string): boolean => CHANG2_STEPS.has(blStep) || (!CHANG1_STEPS.has(blStep) && CHANG2_RUN_STEPS.has(runStep));

const stop = (code: ContinueStopCode, text: string): ContinueDecision => ({ kind: 'stop', code, text });

/**
 * Bảng B11 — THUẦN, một nguồn duy nhất cho nút «Chạy tiếp».
 *  - hết `blocked` ⇒ chỉ chạy lại.
 *  - LEDGER_MISSING / PRICE_UNKNOWN, hoặc CAP_REACHED mà không biết `required` ⇒ DỪNG (không nâng, không đoán 0).
 *  - OVER_RESERVATION ⇒ nâng: need = làm tròn LÊN xu(đã dùng + giữ chỗ); mới = max(need, xu kế trên mức hiện tại).
 *  - CAP_REACHED ⇒ nâng: need = max(ước Chặng 1 [chỉ Chặng 1], đã dùng + giữ chỗ + required); mới = làm tròn LÊN 0,05; nếu ≤ mức hiện tại ⇒ bội 0,05 kế trên mức hiện tại.
 *  - mới > ceiling (mọi trường hợp nâng) ⇒ DỪNG; KHÔNG kẹp xuống ceiling (kẹp xuống sẽ gửi mức không đủ chạy).
 * `required` = max(lời gọi kế tiếp, ước phần còn lại của đúng bước đang chờ [Chặng 2]).
 */
export const continueDecision = (b: NewStoryBudgetView | null | undefined, extra: WaitBudgetExtra = {}): ContinueDecision => {
  if (!b || !isNum(b.capUsd) || !isNum(b.spentUsd)) return stop('NO_LEDGER', STOP_NO_LEDGER_TEXT);
  const bl = b.blocked ?? null;
  if (!bl) return { kind: 'resume' };
  if (bl.reason === 'LEDGER_MISSING' || bl.reason === 'PRICE_UNKNOWN') return stop('UNKNOWN', STOP_UNKNOWN_TEXT);
  const spent = b.spentUsd;
  const reserved = isNum(b.reservedUsd) ? b.reservedUsd : 0;
  const ceiling = capCeilingOf(b.estimatedMaxUsd);
  let newCap: number;
  if (bl.reason === 'OVER_RESERVATION') {
    newCap = Math.max(roundUpCent(spent + reserved), nextCent(b.capUsd));
  } else {
    const blStep = typeof bl.step === 'string' ? bl.step.trim() : '';
    const chang2 = isChang2(blStep, typeof extra.runStep === 'string' ? extra.runStep : '');
    const nextCall = isNum(bl.requiredWorstUsd) && bl.requiredWorstUsd > 0 ? bl.requiredWorstUsd : null;
    // 3h5b R2: Chặng 2 BẮT BUỘC có ước phần còn lại của ĐÚNG bước đang chờ (hợp lệ, > 0, cùng nhãn bước). Thiếu / null / 0 / của bước khác ⇒ DỪNG; giá một lời gọi không thay được ước cả phần còn lại. Chặng 1 giữ luật riêng.
    let stepMax: number | null = null;
    if (chang2) {
      const se = extra.stepEstimate;
      if (!se || !isNum(se.maxUsd) || !(se.maxUsd > 0) || (typeof se.step === 'string' && se.step.trim() !== blStep)) return stop('UNKNOWN', STOP_UNKNOWN_TEXT);
      stepMax = se.maxUsd;
    } else if (nextCall === null) return stop('UNKNOWN', STOP_UNKNOWN_TEXT);
    const required = Math.max(nextCall ?? 0, stepMax ?? 0);
    const floor = !chang2 && isNum(b.estimatedMaxUsd) ? b.estimatedMaxUsd : 0;
    newCap = roundUpTo05(Math.max(floor, spent + reserved + required));
    if (newCap <= b.capUsd + EPS) newCap = roundUpTo05(Math.floor(b.capUsd * 20 + EPS) / 20 + 0.05);
  }
  if (newCap > ceiling + EPS) return stop('CEILING', STOP_CEILING_TEXT);
  return { kind: 'raise', newCapUsd: newCap, expectedRevision: b.revision };
};

export interface WaitBudgetView {
  label: string;
  /** Câu chính dưới nhãn — KHÔNG số tiền. */
  sentence: string;
  /** `continue`: «Chạy tiếp» sẽ gọi route nâng-và-chạy-tiếp một lần; `resume`: chỉ chạy lại; `stuck`: không đi tiếp được (nút «Chạy tiếp» mờ). */
  mode: 'continue' | 'resume' | 'stuck';
  canContinue: boolean;
  /** Lý do khoá nút «Chạy tiếp» (null khi bấm được). */
  disabledReason: string | null;
  /** Quyết định B11 do FE tính — CHỈ dùng cho cầu nối 3h5a (`viaServer=false`). Với `viaServer=true` BE quyết định, FE chỉ gửi `revision`. */
  decision: ContinueDecision;
  /** 3h5b: nút «Chạy tiếp» gọi route `continue-auto-budget` (BE sở hữu trần) thay vì cầu nối. */
  viaServer: boolean;
  revision: number | null;
  /** Dòng chú thích riêng của Chặng 2 (phần dở sẽ làm lại); null ở Chặng 1. */
  note: string | null;
}

export const WAIT_BUDGET_LABEL = 'Chờ bạn quyết · Chặng 1/3';
export const WAIT_BUDGET_LABEL_C2 = 'Chờ bạn quyết · Chặng 2/3';
export const WAIT_BUDGET_NOTE_C2 = 'Phần đang làm dở sẽ làm lại';

export const waitBudgetView = (b: NewStoryBudgetView | null | undefined, extra: WaitBudgetExtra = {}): WaitBudgetView => {
  const bl = b?.blocked ?? null;
  const blStep = typeof bl?.step === 'string' ? bl.step.trim() : '';
  const chang2 = isChang2(blStep, typeof extra.runStep === 'string' ? extra.runStep : '');
  const label = chang2 ? WAIT_BUDGET_LABEL_C2 : WAIT_BUDGET_LABEL;
  const note = chang2 ? WAIT_BUDGET_NOTE_C2 : null;
  const decision = continueDecision(b, extra);
  const c = extra.continuation ?? null;
  // 3h5b: BE có `continuation` (kèm revision hợp lệ) ⇒ BE quyết định; FE chỉ dựng chữ. Thiếu/hỏng ⇒ cầu nối 3h5a như cũ.
  if (c && !c.available) {
    const text = c.reasonCode === 'CEILING_EXCEEDED' ? STOP_CEILING_TEXT : c.reasonCode === 'NO_LEDGER' ? STOP_NO_LEDGER_TEXT : STOP_UNKNOWN_TEXT;
    return { label, note, sentence: text, mode: 'stuck', canContinue: false, disabledReason: text, decision, viaServer: true, revision: typeof c.revision === 'number' ? c.revision : null };
  }
  if (c && typeof c.revision === 'number' && Number.isInteger(c.revision) && c.revision >= 0) {
    const sentence = !bl
      ? 'Đã sẵn sàng — bấm “Chạy tiếp” để chạy bước kế'
      : bl.reason === 'OVER_RESERVATION'
        ? 'Một lần gọi AI tốn hơn mức máy đã tính trước — bấm “Chạy tiếp” để làm tiếp'
        : `Chuỗi đang chờ bạn để ${stepName(bl.step)} — bấm “Chạy tiếp” để làm tiếp`;
    return { label, note, sentence, mode: 'continue', canContinue: true, disabledReason: null, decision, viaServer: true, revision: c.revision };
  }
  const revision = b && isNum(b.revision) ? b.revision : null;
  if (decision.kind === 'stop') {
    return { label, note, sentence: decision.text, mode: 'stuck', canContinue: false, disabledReason: decision.text, decision, viaServer: false, revision };
  }
  if (decision.kind === 'resume') {
    return { label, note, sentence: 'Đã sẵn sàng — bấm “Chạy tiếp” để chạy bước kế', mode: 'resume', canContinue: true, disabledReason: null, decision, viaServer: false, revision };
  }
  const sentence = bl?.reason === 'OVER_RESERVATION'
    ? 'Một lần gọi AI tốn hơn mức máy đã tính trước — bấm “Chạy tiếp” để làm tiếp'
    : `Chuỗi đang chờ bạn để ${stepName(bl?.step)} — bấm “Chạy tiếp” để làm tiếp`;
  return { label, note, sentence, mode: 'continue', canContinue: true, disabledReason: null, decision, viaServer: false, revision };
};

export interface ContinueOutcomeView { level: 'success' | 'warning'; text: string }
/** Kết quả POST nâng-và-chạy-tiếp. KHÔNG in `resumeError.message` của BE (có thể mang số). */
export const continueOutcomeView = (r: NewStoryRaiseCapResult): ContinueOutcomeView => (r.resumed
  ? { level: 'success', text: 'Đã xác nhận — chuỗi chạy tiếp' }
  : { level: 'warning', text: 'Đã xác nhận nhưng chưa chạy tiếp được — bấm “Chạy tiếp” khi sẵn sàng' });

/** 3h5b: kết quả route `continue-auto-budget`. KHÔNG in mã/ message của BE. */
export const autoContinueOutcomeView = (r: NewStoryContinueResult): ContinueOutcomeView => (r.resumed
  ? { level: 'success', text: 'Đã xác nhận — chuỗi chạy tiếp' }
  : { level: 'warning', text: 'Đã xác nhận nhưng chưa chạy tiếp được — bấm “Chạy tiếp” khi sẵn sàng' });

/**
 * 3h5b lượt 2 (R4) — danh tính MỘT thao tác «Chạy tiếp»: bộ `{storyId, runId, expectedRevision, requestId}` gửi BE. Thao tác CHƯA RÕ KẾT QUẢ (mất response sau commit, 5xx/502/timeout, rớt mạng)
 * phải được gửi lại với ĐÚNG bộ cũ — kể cả khi revision của view đã tiến sau khi tải lại dữ liệu — để BE nhận ra replay (không nâng/mở thêm). Id mới chỉ sinh sau khi đã có thành công
 * hoặc lỗi nghiệp vụ chứng minh BE chưa ghi gì.
 */
export interface PendingContinue { storyId: string; runId: string; expectedRevision: number; requestId: string }
export interface PendingStore {
  get(): PendingContinue | null;
  set(p: PendingContinue | null): void;
  /** Đồng bộ (cùng tick, không `await` giữa đọc và ghi): chỉ đổi sang `next` khi bộ đang lưu KHỚP đủ {storyId, runId, requestId, expectedRevision} với `expected`. Trả có đổi hay không. */
  compareAndSet(expected: PendingContinue, next: PendingContinue | null): boolean;
}
const samePending = (a: PendingContinue | null, b: PendingContinue | null): boolean =>
  !!a && !!b && a.storyId === b.storyId && a.runId === b.runId && a.requestId === b.requestId && a.expectedRevision === b.expectedRevision;

/** Có bộ đang dở cho CÙNG truyện + run ⇒ dùng lại NGUYÊN VẸN (không đổi revision); không thì sinh bộ mới từ revision hiện tại của view. */
export const pendingFor = (prev: PendingContinue | null | undefined, ctx: { storyId: string; runId: string; revision: number }, gen: () => string): PendingContinue =>
  (prev && prev.storyId === ctx.storyId && prev.runId === ctx.runId ? prev : { storyId: ctx.storyId, runId: ctx.runId, expectedRevision: ctx.revision, requestId: gen() });

/** Lỗi HTTP 4xx (trừ 408/429, thường do proxy/hạ tầng) = BE từ chối TRƯỚC khi ghi ⇒ chứng minh chưa commit. 5xx, 408, 429, không có response ⇒ không chứng minh được. */
const provesNotCommitted = (e: unknown): boolean => {
  const st = (e as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof st === 'number' && st >= 400 && st < 500 && st !== 408 && st !== 429;
};
export const pendingAfterError = (pending: PendingContinue | null, e: unknown): PendingContinue | null => (provesNotCommitted(e) ? null : pending);

/** Một lần bấm: lấy/ tạo bộ, LƯU trước khi gọi (lần gọi chồng nhau và lần tải lại trang đều thấy), thành công ⇒ xoá, lỗi ⇒ theo `pendingAfterError`. */
export const submitContinue = async (
  ctx: { storyId: string; runId: string; revision: number },
  deps: { store: PendingStore; post: (b: PendingContinue) => Promise<NewStoryContinueResult>; gen?: () => string },
): Promise<{ ok: true; result: NewStoryContinueResult } | { ok: false; error: unknown }> => {
  const pending = pendingFor(deps.store.get(), ctx, deps.gen ?? genRequestId);
  deps.store.set(pending);
  try {
    const result = await deps.post(pending);
    // R4 lượt 3: response đến MUỘN (instance cũ / sau khi thao tác khác đã thay pending) chỉ được đổi pending của CHÍNH request này — compare-and-update.
    deps.store.compareAndSet(pending, null);
    return { ok: true, result };
  } catch (error) {
    deps.store.compareAndSet(pending, pendingAfterError(pending, error));
    return { ok: false, error };
  }
};

const memoryPending = new Map<string, PendingContinue>();
const PENDING_KEY = (storyId: string): string => `ns-continue-pending:${storyId}`;
const isPending = (x: unknown): x is PendingContinue => {
  const p = x as Partial<PendingContinue> | null;
  return !!p && typeof p.storyId === 'string' && typeof p.runId === 'string' && typeof p.requestId === 'string' && typeof p.expectedRevision === 'number' && Number.isInteger(p.expectedRevision);
};
/**
 * Kho bộ đang dở theo truyện: `sessionStorage` (sống qua tải lại trang trong tab; bọc try/catch — private window / chặn site data) VÀ bộ nhớ trong phiên làm lưới lùi
 * (sống qua refresh dữ liệu / remount component khi storage không dùng được).
 */
const writePending = (storyId: string, p: PendingContinue | null): void => {
  if (p) memoryPending.set(storyId, p); else memoryPending.delete(storyId);
  try { if (p) window.sessionStorage.setItem(PENDING_KEY(storyId), JSON.stringify(p)); else window.sessionStorage.removeItem(PENDING_KEY(storyId)); } catch { /* chỉ còn bộ nhớ */ }
};
export const sessionPendingStore = (storyId: string): PendingStore => {
  const get = (): PendingContinue | null => {
    try {
      const raw = window.sessionStorage.getItem(PENDING_KEY(storyId));
      if (raw) { const p = JSON.parse(raw); if (isPending(p) && p.storyId === storyId) return p; return null; }
    } catch { /* storage hỏng / JSON lạ ⇒ lùi về bộ nhớ */ }
    return memoryPending.get(storyId) ?? null;
  };
  return {
    get,
    set: (p) => writePending(storyId, p),
    compareAndSet: (expected, next) => {
      if (!samePending(get(), expected)) return false;
      writePending(storyId, next);
      return true;
    },
  };
};
/** Sinh `requestId` (UUID khi có `crypto.randomUUID`, không thì chuỗi ngẫu nhiên đủ dài; khớp `[A-Za-z0-9._:-]{8,100}` của BE). */
export const genRequestId = (): string => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `rq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const dataOf = (e: unknown): any => (e as any)?.response?.data ?? {};
const CONTINUE_RELOAD_CODES: ReadonlySet<string> = new Set(['REVISION_MISMATCH', 'CHAIN_MOVED', 'LEDGER_MISSING', 'BUDGET_BLOCKED', 'NO_CHAIN',
  // 3h5b (route continue-auto-budget): chuỗi/sổ/ước đã đổi ⇒ tải lại để lấy `continuation` mới.
  'ESTIMATE_STALE', 'RUN_MISMATCH', 'NOT_WAITING', 'CONTINUE_RETRY',
  // F5: BE từ chối mức FE vừa tính (CAP_*) = FE lệch sổ/luật ⇒ tải lại để lần bấm sau tính trên sổ mới, không gửi lại đúng mức cũ.
  'CAP_INVALID', 'CAP_NOT_HIGHER', 'CAP_BELOW_SPENT', 'CAP_TOO_HIGH']);
const CONTINUE_RELOAD_TEXT = 'Trạng thái chuỗi vừa đổi — đã tải lại, xem rồi bấm “Chạy tiếp” lần nữa';
const CONTINUE_GENERIC_TEXT = 'Chưa chạy tiếp được — thử lại sau, nếu vẫn lỗi báo kỹ thuật';

/** Lỗi POST «Chạy tiếp»: map THEO MÃ; không bao giờ in `message` của BE. */
export const continueErrorView = (e: unknown): { text: string; reload: boolean } => {
  const d = dataOf(e);
  if (typeof d.code === 'string' && CONTINUE_RELOAD_CODES.has(d.code)) return { text: CONTINUE_RELOAD_TEXT, reload: true };
  return { text: CONTINUE_GENERIC_TEXT, reload: false };
};

/** 3h5b: lỗi POST `continue-auto-budget` — map THEO MÃ; `CONTINUE_UNKNOWN`/`CONTINUE_CEILING` = BE từ chối nâng (câu cố định, tải lại để dải hiện nút mờ). Không bao giờ in `message` của BE. */
export const autoContinueErrorView = (e: unknown): { text: string; reload: boolean } => {
  const code = dataOf(e).code;
  if (code === 'CONTINUE_UNKNOWN') return { text: STOP_UNKNOWN_TEXT, reload: true };
  if (code === 'CONTINUE_CEILING') return { text: STOP_CEILING_TEXT, reload: true };
  return continueErrorView(e);
};

// ─── Khung «Bắt đầu» ────────────────────────────────────────────────────────

const START_LOADING_TEXT = 'Đang kiểm tra điều kiện chạy…';
const START_UNKNOWN_TEXT = 'Máy chưa kiểm tra được điều kiện chạy — báo kỹ thuật';

export interface EstimateButton { label: string; disabled: boolean; reason: string | null; /** SỐ THÔ của BE — đúng giá trị gửi `expectedMaxUsd` (không hiện). */ expectedMaxUsd: number | null }
export interface EstimateView {
  state: 'loading' | 'unknown' | 'ready';
  button: EstimateButton;
}

/** `undefined` = đang tải; `null` = tải lỗi. Chưa có số ước dương ⇒ nút xám (BE bắt buộc `expectedMaxUsd`); KHÔNG BAO GIỜ gửi 0. */
export const estimateView = (est: NewStoryEstimate | null | undefined): EstimateView => {
  if (est === undefined) return { state: 'loading', button: { label: 'Bắt đầu', disabled: true, reason: START_LOADING_TEXT, expectedMaxUsd: null } };
  const max = est?.chang1?.maxUsd;
  if (!est || !est.chang1 || !isNum(max) || max <= 0) return { state: 'unknown', button: { label: 'Bắt đầu', disabled: true, reason: START_UNKNOWN_TEXT, expectedMaxUsd: null } };
  return { state: 'ready', button: { label: 'Bắt đầu', disabled: false, reason: null, expectedMaxUsd: max } };
};

export interface StartErrorView { kind: 'changed' | 'unknown' | 'other'; text: string; newMaxUsd: number | null }
/** Lỗi POST Bắt đầu: map THEO MÃ, không in `message`/`unknownReasons` của BE. `newMaxUsd` (nếu có) chỉ là dữ liệu. */
export const startErrorView = (e: unknown): StartErrorView => {
  const d = dataOf(e);
  if (d.code === 'ESTIMATE_CHANGED') return { kind: 'changed', newMaxUsd: isNum(d.maxUsd) && d.maxUsd > 0 ? d.maxUsd : null, text: 'Điều kiện chạy vừa đổi — đã tính lại, bấm Bắt đầu lần nữa' };
  if (d.code === 'ESTIMATE_UNKNOWN') return { kind: 'unknown', newMaxUsd: null, text: START_UNKNOWN_TEXT };
  if (d.code === 'EXPECTED_MAX_REQUIRED') return { kind: 'other', newMaxUsd: null, text: 'Thiếu thông tin đã xem — tải lại trang rồi thử lại' };
  return { kind: 'other', newMaxUsd: null, text: 'Không bắt đầu được — thử lại sau' };
};

// ─── Thẻ STOP 1 («Duyệt & chạy tiếp») ────────────────────────────────────────

export const STOP1_BUTTON_LABEL = 'Duyệt & chạy tiếp';
const STOP1_NO_ESTIMATE = 'Chưa kiểm tra được điều kiện chạy Chặng 2 — tải lại trang rồi thử lại';

export interface Stop1Button { label: string; disabled: boolean; reason: string | null; /** SỐ THÔ của BE (không làm tròn) — đúng giá trị gửi `expectedMaxUsd`. */ expectedMaxUsd: number | null }
export interface Stop1View {
  state: 'ready' | 'unknown';
  /** Lý do chưa chạy được (state unknown); null khi ready. */
  reasonLine: string | null;
  button: Stop1Button;
}

/** Một nguồn cho nút «Duyệt & chạy tiếp»: số gửi `expectedMaxUsd` là `maxUsd` thô của BE. Không có số dương ⇒ nút khoá (BE bắt buộc), KHÔNG gửi 0. */
export const stop1View = (e: Stop1Estimate | null | undefined): Stop1View => {
  if (!e || e.state !== 'ready' || !isNum(e.maxUsd) || e.maxUsd <= 0) {
    return { state: 'unknown', reasonLine: STOP1_NO_ESTIMATE, button: { label: STOP1_BUTTON_LABEL, disabled: true, reason: STOP1_NO_ESTIMATE, expectedMaxUsd: null } };
  }
  return { state: 'ready', reasonLine: null, button: { label: STOP1_BUTTON_LABEL, disabled: false, reason: null, expectedMaxUsd: e.maxUsd } };
};

export interface Stop1ErrorView {
  /** `changed`: điều kiện đổi (toast + tải lại, KHÔNG tự gửi lại) · `unknown`: khoá · `reload`: chuỗi/sổ đã đổi ⇒ báo ngắn rồi tải lại · `other`: để nhánh cũ lo. */
  kind: 'changed' | 'unknown' | 'reload' | 'other';
  text: string;
  newMaxUsd: number | null;
  reload: boolean;
}
export const STOP1_CHANGED_TEXT = 'Điều kiện chạy vừa đổi — xem lại rồi bấm lại';
const STOP1_RELOAD_TEXT: Record<string, string> = {
  RUN_ADVANCED: 'Chuỗi đã chạy tiếp rồi — đã tải lại để xem trạng thái mới',
  CHAIN_MOVED: 'Chuỗi vừa được mở bước kế ở nơi khác — đã tải lại để xem trạng thái mới',
  BUDGET_BLOCKED: 'Chuỗi vừa bị chặn ở bước này — đã tải lại để xem',
  OVER_RESERVATION_NEEDS_CAP: 'Một lần gọi AI vừa tốn hơn mức máy đã tính trước — đã tải lại, bấm “Chạy tiếp” để làm tiếp',
  EXPECTED_MAX_REQUIRED: 'Thiếu thông tin đã xem — đã tải lại, xem rồi bấm lại',
};
/** Lỗi POST «Duyệt & chạy tiếp» ở STOP 1: map THEO MÃ, không in `message` của BE. Lỗi khác (`OUTLINE_CHANGED`…) ⇒ `other`, `text` rỗng — nhánh cũ lo. */
export const stop1ErrorView = (e: unknown): Stop1ErrorView => {
  const d = dataOf(e);
  if (d.code === 'ESTIMATE_CHANGED') return { kind: 'changed', text: STOP1_CHANGED_TEXT, newMaxUsd: isNum(d.maxUsd) && d.maxUsd > 0 ? d.maxUsd : null, reload: true };
  if (d.code === 'ESTIMATE_UNKNOWN') return { kind: 'unknown', newMaxUsd: null, reload: true, text: STOP1_NO_ESTIMATE };
  if (typeof d.code === 'string' && STOP1_RELOAD_TEXT[d.code]) return { kind: 'reload', text: STOP1_RELOAD_TEXT[d.code], newMaxUsd: null, reload: true };
  return { kind: 'other', text: '', newMaxUsd: null, reload: false };
};
